import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, ArrowUpRight, X } from "lucide-react";
import { IR_PORTAL_URL } from "../../lib/nav";
import { askEqva } from "../ask/AskEqva";
import { t } from "../../lib/i18n";

/* The early way-finder. A small card in the quiet bottom-right corner, once per session, shortly after arrival:
   four numbered doors that route straight to where each visitor belongs – investors out to the external IR portal,
   the rest to the right page. «Spør EQVA» is only the fallback line at the bottom.
   Never a modal. A hairline along the top shows it will retire by itself; it waits while you read (hover/focus). */

const KEY = "eqva.wayfinder";
const SHOW_AFTER_MS = 3500;      // …or as soon as the visitor scrolls a little
const SCROLL_TRIGGER_PX = 240;
const LINGER_MS = 24000;
const TICK_MS = 200;

const DOORS = [
  { id: "investor", label: t("Jeg er investor"), sub: t("Investorportalen · ir.eqva.no"), href: IR_PORTAL_URL, external: true },
  { id: "prosjekt", label: t("Jeg trenger en leverandør"), sub: t("Ti industriselskaper, ett sted"), href: "/selskaper" },
  { id: "jobb", label: t("Jeg ser etter jobb"), sub: t("Ledige stillinger og læreplasser"), href: "/om-oss/karriere" },
  { id: "eierskifte", label: t("Jeg vurderer å selge selskapet mitt"), sub: t("Slik tenker EQVA som eier"), href: "/bli-en-del-av-eqva" },
];

const load = () => { try { return sessionStorage.getItem(KEY) === "1"; } catch { return false; } };
const mark = () => { try { sessionStorage.setItem(KEY, "1"); } catch { /* private mode */ } };

export const WayFinder = () => {
  const { pathname } = useLocation();
  const [show, setShow] = useState(false);
  const [left, setLeft] = useState(LINGER_MS);   // time left before it retires – frozen while hovered/focused
  const [held, setHeld] = useState(false);
  const done = useRef(load());

  const retire = useCallback(() => { setShow(false); if (!done.current) { done.current = true; mark(); } }, []);

  /* Arm once: after a short pause, or on the first real scroll – whichever comes first. */
  useEffect(() => {
    if (done.current) return undefined;
    let fired = false;
    const fire = () => { if (fired) return; fired = true; setShow(true); window.removeEventListener("scroll", onScroll); };
    const onScroll = () => { if (window.scrollY > SCROLL_TRIGGER_PX) fire(); };
    const t = setTimeout(fire, SHOW_AFTER_MS);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { clearTimeout(t); window.removeEventListener("scroll", onScroll); };
  }, [pathname]);

  /* Retires by itself – the clock only runs while nobody is reading – and the moment Spør EQVA opens by other means. Esc closes. */
  useEffect(() => {
    if (!show) return undefined;
    const tick = setInterval(() => { if (!held) setLeft((v) => Math.max(0, v - TICK_MS)); }, TICK_MS);
    const onAsk = () => retire();
    const onKey = (e) => { if (e.key === "Escape") retire(); };
    window.addEventListener("eqva:ask", onAsk);
    window.addEventListener("keydown", onKey);
    return () => { clearInterval(tick); window.removeEventListener("eqva:ask", onAsk); window.removeEventListener("keydown", onKey); };
  }, [show, held, retire]);
  useEffect(() => { if (show && left === 0) retire(); }, [show, left, retire]);

  const ask = () => { retire(); askEqva(""); };
  const hold = () => setHeld(true), release = () => setHeld(false);

  return (
    <div data-testid="wayfinder" role="region" aria-label={t("Hva leter du etter?")} aria-hidden={!show}
      className={`wayfinder ${show ? "is-shown" : ""}`}
      onMouseEnter={hold} onMouseLeave={release} onFocus={hold} onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) release(); }}>
      <span className="wayfinder-clock" aria-hidden="true" style={{ transform: `scaleX(${left / LINGER_MS})` }} />
      <div className="wayfinder-head">
        <h2 className="wayfinder-title">{t("Hva leter du etter?")}</h2>
        <button type="button" data-testid="wayfinder-close" aria-label={t("Lukk")} onClick={retire} className="wayfinder-x"><X size={18} strokeWidth={1.75} /></button>
      </div>
      <ul className="wayfinder-doors">
        {DOORS.map((d, i) => {
          const inner = (
            <>
              <span className="wayfinder-num">0{i + 1}</span>
              <span className="min-w-0 flex-1">
                <span className="wayfinder-label">{d.label}</span>
                <span className="wayfinder-sub">{d.sub}</span>
              </span>
              {d.external
                ? <ArrowUpRight size={16} strokeWidth={1.75} className="wayfinder-arrow is-out" />
                : <ArrowRight size={16} strokeWidth={1.75} className="wayfinder-arrow" />}
            </>
          );
          return (
            <li key={d.id} className="wayfinder-item" style={{ "--i": i }}>
              {d.external
                ? <a href={d.href} target="_blank" rel="noreferrer" data-testid={`wayfinder-${d.id}`} onClick={retire} className="wayfinder-door">{inner}</a>
                : <Link to={d.href} data-testid={`wayfinder-${d.id}`} onClick={retire} className="wayfinder-door">{inner}</Link>}
            </li>
          );
        })}
      </ul>
      <p className="wayfinder-foot">
        <span>{t("Noe annet?")}</span>
        <button type="button" data-testid="wayfinder-ask" onClick={ask} className="wayfinder-ask">{t("Spør EQVA")} <ArrowRight size={14} strokeWidth={1.75} aria-hidden="true" /></button>
      </p>
    </div>
  );
};
